import { Store } from '@ngrx/store';
import { filter, tap, map, switchMap, take, withLatestFrom } from 'rxjs/operators';
import { setLoading } from './loading/actions';
import { Observable, of, combineLatest } from 'rxjs';

/**
 * Creates a caching guard for ngrx effects that prevents redundant data fetching.
 *
 * This helper checks:
 * 1. If data already exists in the store
 * 2. If a fetch is currently in progress (loading state)
 * 3. If forceRefresh flag is set (bypasses cache)
 *
 * Usage in effects:
 * ```typescript
 * loadItems$ = createEffect(() => {
 *   return this.actions$.pipe(
 *     ofType(MyActions.loadItems),
 *     shouldLoadData(
 *       this.store,
 *       'myCollection',
 *       (action) => action.barId,
 *       MySelectors.selectAll
 *     ),
 *     switchMap((action) => ...)
 *   );
 * });
 * ```
 *
 * @param store - NgRx Store instance
 * @param loadingKey - Unique key for tracking loading state (e.g., 'beverages', 'kegs')
 * @param getBarId - Function to extract barId from action (for cache key uniqueness)
 * @param dataSelector - Selector to get existing data from store
 * @returns RxJS operator that filters out unnecessary loads
 */
export function shouldLoadData<T, A extends { forceRefresh?: boolean }>(
  store: Store,
  loadingKey: string,
  getBarId: (action: A) => string,
  dataSelector: any
) {
  return (source: Observable<A>) => source.pipe(
    switchMap(action =>
      combineLatest([
        of(action),
        store.select(dataSelector).pipe(take(1)),
        store.select((state: any) => state.loading?.[`${loadingKey}_${getBarId(action)}`] || false).pipe(take(1))
      ])
    ),
    filter(([action, existingData, isLoading]: [A, any, boolean]) => {
      // Always load if forceRefresh is true
      if (action.forceRefresh) {
        return true;
      }

      // Skip if currently loading
      if (isLoading) {
        console.log(`[Effect Cache] Skipping ${loadingKey}: already loading`);
        return false;
      }

      // Skip if data already exists
      if (existingData && existingData.length > 0) {
        console.log(`[Effect Cache] Skipping ${loadingKey}: data already loaded (${existingData.length} items)`);
        return false;
      }

      // Load if no data exists and not loading
      return true;
    }),
    map(([action]) => action)
  );
}

/**
 * Wraps effect data fetching with loading state management.
 *
 * Automatically dispatches loading start/end actions around the data fetch.
 *
 * Usage in effects:
 * ```typescript
 * loadItems$ = createEffect(() => {
 *   return this.actions$.pipe(
 *     ofType(MyActions.loadItems),
 *     switchMap((action) =>
 *       trackLoading(
 *         this.store,
 *         'myCollection',
 *         action.barId,
 *         this.myService.getItems(action.barId).pipe(
 *           map(items => MyActions.loadItemsSuccess({ items })),
 *           catchError(error => of(MyActions.loadItemsFailure({ error })))
 *         )
 *       )
 *     )
 *   );
 * });
 * ```
 *
 * @param store - NgRx Store instance
 * @param loadingKey - Unique key for tracking loading state
 * @param barId - Current bar ID for unique loading key
 * @param source$ - Observable to wrap with loading tracking
 * @returns Observable that dispatches loading actions
 */
export function trackLoading<T>(
  store: Store,
  loadingKey: string,
  barId: string,
  source$: Observable<T>
): Observable<T> {
  const key = `${loadingKey}_${barId}`;

  // Set loading to true before fetch
  store.dispatch(setLoading({ key, loading: true }));

  return source$.pipe(
    tap({
      next: () => store.dispatch(setLoading({ key, loading: false })),
      error: () => store.dispatch(setLoading({ key, loading: false })),
      complete: () => store.dispatch(setLoading({ key, loading: false }))
    })
  );
}

/**
 * Simplified caching check for effects that don't use barId.
 *
 * @param store - NgRx Store instance
 * @param loadingKey - Unique key for tracking loading state
 * @param dataSelector - Selector to get existing data from store
 * @returns RxJS operator that filters out unnecessary loads
 */
export function shouldLoadDataSimple<T, A extends { forceRefresh?: boolean }>(
  store: Store,
  loadingKey: string,
  dataSelector: any
) {
  return (source: Observable<A>) => source.pipe(
    withLatestFrom(
      store.select(dataSelector),
      store.select((state: any) => state.loading?.[loadingKey] || false)
    ),
    filter(([action, existingData, isLoading]: [A, any, boolean]) => {
      if (action.forceRefresh) {
        return true;
      }

      if (isLoading) {
        console.log(`[Effect Cache] Skipping ${loadingKey}: already loading`);
        return false;
      }

      if (existingData && existingData.length > 0) {
        console.log(`[Effect Cache] Skipping ${loadingKey}: data already loaded (${existingData.length} items)`);
        return false;
      }

      return true;
    }),
    map(([action]) => action)
  );
}

/**
 * Simple loading tracker for effects without barId.
 *
 * @param store - NgRx Store instance
 * @param loadingKey - Unique key for tracking loading state
 * @param source$ - Observable to wrap with loading tracking
 * @returns Observable that dispatches loading actions
 */
export function trackLoadingSimple<T>(
  store: Store,
  loadingKey: string,
  source$: Observable<T>
): Observable<T> {
  store.dispatch(setLoading({ key: loadingKey, loading: true }));

  return source$.pipe(
    tap({
      next: () => store.dispatch(setLoading({ key: loadingKey, loading: false })),
      error: () => store.dispatch(setLoading({ key: loadingKey, loading: false })),
      complete: () => store.dispatch(setLoading({ key: loadingKey, loading: false }))
    })
  );
}
